import { Link } from "react-router-dom";
import { useLang, useLp, useT } from "../lib/i18n";
import {
  getNode,
  getRelated,
  type InternalLinkKind,
  type InternalLinkNode,
} from "../lib/seo/internalLinks";

interface RelatedListProps {
  /** Heading rendered above the cards. */
  heading: string;
  /** Optional sub-copy under the heading. */
  lede?: string;
  /** Resolved related nodes (typically 2–3). */
  items: InternalLinkNode[];
}

/**
 * Generic related-block presentation. Renders a heading + grid of card-style
 * links. Two thin wrappers below (`RelatedComparisons`, `RelatedUseCases`)
 * resolve the right kind from the graph and skip rendering when the slate is
 * empty — e.g. a comparison page with no other published comparisons should
 * not render a stub block.
 */
function RelatedList({ heading, lede, items }: RelatedListProps) {
  const lp = useLp();
  const lang = useLang();
  if (items.length === 0) return null;

  return (
    <section className="border-b border-margin dark:border-paper/10">
      <div className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper">
          {heading}
        </h2>
        {lede && (
          <p className="mt-4 text-[15px] text-ink/65 dark:text-paper/65">{lede}</p>
        )}
        <ul className="mt-10 grid gap-5 md:grid-cols-3">
          {items.map((node) => (
            <li key={node.id}>
              <Link
                to={lp + node.path}
                className="group block h-full rounded-2xl border border-ink/10 dark:border-paper/12 bg-ink/[0.02] dark:bg-paper/[0.02] p-6 transition-colors hover:border-ink/25 dark:hover:border-paper/25"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
                  {kindLabel(node.kind, lang)}
                </p>
                <h3 className="mt-3 font-display text-lg font-semibold text-ink dark:text-paper">
                  {node.label[lang]}
                </h3>
                {node.blurb && (
                  <p className="mt-3 text-[14px] leading-relaxed text-ink/70 dark:text-paper/70">
                    {node.blurb[lang]}
                  </p>
                )}
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55 group-hover:text-ink dark:group-hover:text-paper">
                  {ctaLabel(lang)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Bottom-of-page block listing other published comparison pages. */
export function RelatedComparisons({ currentId }: { currentId: string }) {
  const t = useT();
  const items = getRelated(currentId, "comparison");
  return (
    <RelatedList
      heading={t.relatedLinks.comparisonsHeading}
      lede={t.relatedLinks.comparisonsLede}
      items={items}
    />
  );
}

/** Bottom-of-page block listing other published use-case pages. */
export function RelatedUseCases({ currentId }: { currentId: string }) {
  const t = useT();
  const items = getRelated(currentId, "useCase");
  return (
    <RelatedList
      heading={t.relatedLinks.useCasesHeading}
      lede={t.relatedLinks.useCasesLede}
      items={items}
    />
  );
}

/**
 * Inline row of "keep exploring" internal links (blog + downloads by default).
 * Used to guarantee a page hits the body-link minimum even on days when the
 * same-kind related slate is sparse. Skips any id in `excludeIds` and any
 * unpublished node.
 */
export function KeepExploring({
  excludeIds = [],
  ids = ["blog", "downloads"],
}: {
  excludeIds?: string[];
  ids?: string[];
}) {
  const lp = useLp();
  const lang = useLang();
  const t = useT();
  const skip = new Set(excludeIds);
  const items = ids
    .map(getNode)
    .filter((n): n is InternalLinkNode => !!n && n.published && !skip.has(n.id));
  if (items.length === 0) return null;

  return (
    <section>
      <div className="mx-auto max-w-3xl px-6 py-12 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/55 dark:text-paper/55">
          {t.relatedLinks.keepExploring}
        </p>
        <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {items.map((node) => (
            <li key={node.id}>
              <Link
                to={lp + node.path}
                className="text-[14px] text-ink/75 dark:text-paper/75 underline-offset-4 hover:underline hover:text-ink dark:hover:text-paper transition-colors"
              >
                {node.label[lang]}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const KIND_LABELS: Record<InternalLinkKind, Record<"en" | "pt" | "es", string>> = {
  comparison: { en: "Comparison", pt: "Comparação", es: "Comparación" },
  useCase: { en: "Use case", pt: "Caso de uso", es: "Caso de uso" },
  blog: { en: "Blog", pt: "Blog", es: "Blog" },
  core: { en: "Minuta", pt: "Minuta", es: "Minuta" },
};

function kindLabel(kind: InternalLinkKind, lang: "en" | "pt" | "es"): string {
  return KIND_LABELS[kind][lang];
}

function ctaLabel(lang: "en" | "pt" | "es"): string {
  if (lang === "pt") return "Ler mais →";
  if (lang === "es") return "Leer más →";
  return "Read more →";
}
